import { useEffect, useRef, useCallback } from 'react'
import { createDynamicCanvasController } from '../../lib/chart/dynamicCanvas.js'

export function useDynamicCanvas({enabled, viewKey, ready, chartReadyNonce, chartRef, containerRef, candlesRef, timeframe, margins, beforeApply}) {
  const current = useRef(null)
  current.current = {ready, timeframe, margins, beforeApply}
  const controllerRef = useRef(null)
  const signature = JSON.stringify([margins.dynamicSchedule,margins.candlesBeforeOpen])
  useEffect(() => {
    if (!enabled || !chartRef.current) { controllerRef.current=null; return undefined }
    const chart=chartRef.current, container=containerRef.current
    const controller=createDynamicCanvasController({
      getInput: () => current.current.ready ? {
        candles:candlesRef.current, timeframe:current.current.timeframe,
        schedule:current.current.margins.dynamicSchedule,
        candlesBeforeOpen:current.current.margins.candlesBeforeOpen,
        now:Date.now(), timeToIndex: time => chart.timeScale().timeToIndex?.(time),
      } : null,
      apply: range => {
        try { current.current.beforeApply(); chart.timeScale().setVisibleLogicalRange(range); return true } catch { return false }
      },
    })
    controllerRef.current=controller
    try { chart.timeScale().applyOptions({shiftVisibleRangeOnNewBar:false}) } catch { /* disposed */ }
    const tick=()=>controller.tick()
    tick()
    const timer=setInterval(tick,1000)
    const visible=()=>{if(document.visibilityState!=='hidden')tick()}
    document.addEventListener('visibilitychange',visible)
    let start=null
    const down=e=>{if(start)controller.pause();start={x:e.clientX,y:e.clientY}}
    const move=e=>{if(start && Math.hypot(e.clientX-start.x,e.clientY-start.y)>3)controller.pause()}
    const up=()=>{start=null}
    const wheel=()=>controller.pause()
    container?.addEventListener('pointerdown',down,true)
    container?.addEventListener('pointermove',move,true)
    container?.addEventListener('wheel',wheel,{capture:true,passive:true})
    window.addEventListener('pointerup',up)
    window.addEventListener('pointercancel',up)
    return () => {
      clearInterval(timer);document.removeEventListener('visibilitychange',visible)
      container?.removeEventListener('pointerdown',down,true);container?.removeEventListener('pointermove',move,true)
      container?.removeEventListener('wheel',wheel,true);window.removeEventListener('pointerup',up);window.removeEventListener('pointercancel',up)
      if(controllerRef.current===controller)controllerRef.current=null
      try { chart.timeScale().applyOptions({shiftVisibleRangeOnNewBar:true}) } catch { /* disposed */ }
    }
  },[enabled,viewKey,chartReadyNonce,signature,chartRef,containerRef,candlesRef])
  return {
    pause:useCallback(()=>controllerRef.current?.pause(),[]),
    reset:useCallback(()=>controllerRef.current?.reset() || false,[]),
  }
}
